import React, { useState } from 'react';

import ExportedImage from 'next-image-export-optimizer';
import Link from 'next/link';

import { motion } from 'framer-motion';

import BgEllipse from '@/components/shared/lp/BgEllipse';
import { FAQ } from '@/components/shared/lp/komodo-wallet/desktop/FAQ';
import { JoinCommunityMobileDesktop } from '@/components/shared/lp/komodo-wallet/desktop/JoinCommunityMobileDesktop';
import { ListedCryptos500 } from '@/components/shared/lp/komodo-wallet/desktop/ListedCryptos500';
import { MobileWallet } from '@/components/shared/lp/komodo-wallet/desktop/MobileWallet';

import KomodoWalletDownloads from '@/data/KomodoWalletDownloads.json';
import { siteData } from '@/data/configs/site';
import heroopen from '@/public/images/lp/desktop/hero-open.png';
import laptopmock from '@/public/images/lp/desktop/laptop-mock-glass.png';
import footerArrow from '@/public/images/lp/komodo-wallet/footer-arrow.svg';
import linux from '@/public/images/lp/komodo-wallet/linux.svg';
import macos from '@/public/images/lp/komodo-wallet/macos.svg';
import windows from '@/public/images/lp/komodo-wallet/windows.svg';
import kmdlogo from '@/public/images/lp/mobile/kmd-logo-light.svg';

export default function Desktop() {
  const [selected, setSelected] = useState(null);
  const mobileWalletData = [
    {
      header: 'HODL your coins',
      description:
        'Komodo Wallet is a secure place to store your crypto. The app is non-custodial. Your keys. Your coins.',
    },
    {
      header: 'Swap on the DEX',
      description: (
        <span>
          Our decentralized exchange is powered by atomic swap technology.
          <br className='hidden lg:block 2xl:hidden' /> Swap cross-chain, P2P. No third parties or
          AMMs involved.
        </span>
      ),
    },
  ];
  const faqData = [
    {
      faqNo: '1',
      faqQstn: 'Is Komodo Wallet available on other platforms besides desktop?',
      faqAnswer: (
        <span>
          {' '}
          Yes, Komodo Wallet is also available on a{' '}
          <Link href={siteData.webDEXlink} className='anchor-highlights'>
            web browser app
          </Link>{' '}
          and available to download on{' '}
          <Link href='/en/komodo-wallet/mobile/' className='anchor-highlights'>
            mobile
          </Link>{' '}
          (iOS and Android).
        </span>
      ),
    },
    {
      faqNo: '2',
      faqQstn: ' I have a question/comment about Komodo Wallet. Who do I contact?',
      faqAnswer: (
        <span>
          <Link href={siteData.social.discord} className='anchor-highlights'>
            Komodo&apos;s Discord Support channel
          </Link>{' '}
          is the best place to ask. Official team members have KMD logo badges in their usernames.
        </span>
      ),
    },
    {
      faqNo: '3',
      faqQstn: 'Do I need to keep the desktop app open when trading?',
      faqAnswer: (
        <span className='text-base font-medium text-white sm:text-xl lg:text-lg'>
          Yes, atomic swaps require that you are constantly connected to the internet. You&apos;ll
          need to have your Komodo desktop wallet open when trading. It&apos;s fine to keep your app
          running in the background and have other apps running at the same time.
        </span>
      ),
    },
    {
      faqNo: '4',
      faqQstn: 'Is the Komodo desktop wallet available in every country/ region?',
      faqAnswer: (
        <span className='text-base font-medium text-white sm:text-xl lg:text-lg'>
          The Komodo desktop wallet is available to download and use worldwide. Users can HODL
          supported cryptocurrencies in the non-custodial wallet and trade on the built-in
          decentralized exchange.
        </span>
      ),
    },
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const buttonHoverTap = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const downloadButtonWrapper = (children) => (
    <motion.div
      whileHover='hover'
      whileTap='tap'
      variants={buttonHoverTap}
      style={{ display: 'inline-block' }}
    >
      {children}
    </motion.div>
  );

  return (
    <>
      <div className='absolute h-full w-full overflow-hidden'>
        <div className='mobile-header-ellipse-1 absolute'></div>
        <div className='mobile-header-ellipse-2 absolute'></div>
      </div>
      <div className='absolute h-full w-full overflow-hidden'>
        <div className='desktop-bottom-ellipse-1 absolute'></div>
        <div className='desktop-bottom-ellipse-2 absolute'></div>
      </div>
      <div
        className={`absolute -left-[600px] -top-[200px] h-screen w-[1087.432px] bg-gradient-radial from-[rgba(106,77,227,0.26)] to-[rgba(106,77,227,0)]`}
      ></div>
      <main className='container relative mx-auto font-sans text-white md:grid md:grid-cols-24 md:gap-0'>
        <div className='hidden lg:col-span-2 lg:block'></div>
        <div className='relative px-4 xs:px-8 md:col-span-24 lg:col-span-20 lg:px-0'>
          <section className='relative z-20 flex flex-col items-center pt-32 lg:h-screen lg:max-h-[1080px] lg:min-h-[750px] lg:pt-0'>
            <motion.div
              variants={staggerChildren}
              initial='initial'
              animate='animate'
              className='flex w-full flex-col items-center justify-between lg:my-auto lg:h-2/3 lg:flex-row'
            >
              <motion.div
                variants={fadeInUp}
                className='justify-start md:max-w-[75%] lg:w-[400px] xl:w-[407px] 2xl:w-[488px] 3xl:w-[611px]'
              >
                <motion.h1
                  variants={fadeInUp}
                  className='text-left text-2xl font-bold uppercase md:text-center lg:text-left lg:text-[2.5rem] lg:leading-[50px]'
                >
                  Download Komodo
                  <br className='lg:hidden' /> Deskop Wallet
                </motion.h1>
                <motion.p
                  variants={fadeInUp}
                  className='pt-4 text-left text-sm font-medium md:text-center md:text-base lg:text-left 2xl:pt-8 2xl:text-lg'
                >
                  Built by Komodo, Komodo Wallet is the most robust desktop wallet on the market.
                  HODL crypto and trade across dozens of blockchain protocols.
                </motion.p>
                <motion.div
                  variants={fadeInUp}
                  className='mx-auto flex flex-wrap justify-between gap-4 pt-6 xs:justify-start xs:gap-6 sm:flex-nowrap md:justify-center lg:justify-start xl:px-0 2xl:pt-10'
                >
                  {downloadButtonWrapper(
                    <Link href={KomodoWalletDownloads.windows} className='all-desktop-hero-win'>
                      <ExportedImage
                        placeholder='empty'
                        src={windows}
                        alt='windows'
                        className='h-auto w-[147px]'
                      />
                    </Link>,
                  )}
                  {downloadButtonWrapper(
                    <Link href={KomodoWalletDownloads.macos} className='all-desktop-hero-mac'>
                      <ExportedImage
                        placeholder='empty'
                        src={macos}
                        alt='mac-os'
                        className='h-auto w-[147px]'
                      />
                    </Link>,
                  )}
                  {downloadButtonWrapper(
                    <Link href={KomodoWalletDownloads.linux} className='all-desktop-hero-linux'>
                      <ExportedImage
                        placeholder='empty'
                        src={linux}
                        alt='linux'
                        className='h-auto w-[147px]'
                      />
                    </Link>,
                  )}
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <video
                  id='desktop-hero-vid'
                  muted
                  autoPlay
                  playsInline
                  preload='true'
                  className='mt-12 h-auto w-full md:w-[500px] lg:mt-0 lg:w-[420px] xl:w-[552px] 2xl:w-[681px] 3xl:w-[781px]'
                >
                  <source src='/img/desktop/vids/laptop_front_alpha.webm' type='video/webm' />
                  <source
                    src='/img/desktop/vids/laptop_straight0001-HD.mov'
                    type='video/quicktime'
                  />
                </video>
              </motion.div>
              <ExportedImage
                placeholder='empty'
                src={heroopen}
                alt='Komodo Wallet desktop'
                className='mt-12 hidden h-auto w-full md:w-[500px] lg:mt-0 lg:w-[420px] xl:w-[552px] 2xl:w-[681px] 3xl:w-[781px]'
                id='desktop-hero-img'
              />
            </motion.div>

            <Link
              href={siteData.komodoPlatformHome}
              className='relative bottom-[10.5%] lg:absolute'
            >
              <div className='mx-auto flex flex-row items-center pt-20'>
                <p className='mr-3 text-sm font-medium'>Powered by</p>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <ExportedImage
                    placeholder='empty'
                    src={kmdlogo}
                    alt='komodo logo'
                    className='mr-3 h-auto w-[99.5px]'
                  />
                </motion.div>
              </div>
            </Link>
          </section>
          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='mx-auto pt-16 md:pb-[50px] md:pt-20 lg:pb-[150px] lg:pt-10 xl:pt-16 2xl:pt-20 3xl:pb-[200px] 3xl:pt-24'
          >
            <div className='mx-auto mb-20 text-center'>
              <h2 className='text-2xl font-bold xs:text-3xl xl:text-4xl'>
                The Ultimate Desktop Wallet
              </h2>
              <p className='mt-0 pt-4 text-center text-sm font-medium md:-mt-5 xl:text-lg 2xl:pt-8'>
                The best cross-chain bridge in crypto
              </p>
            </div>
            <div className='flex flex-col md:grid md:grid-cols-20 md:flex-row md:gap-0 3xl:pt-16'>
              <div className='md:col-span-11'>
                <ExportedImage
                  placeholder='empty'
                  src={laptopmock}
                  alt='laptop'
                  className='h-auto w-[90%] md:mt-28 lg:w-[85%] xl:w-[90%] 2xl:mt-0'
                />
              </div>
              <div className='mx-auto max-w-[394px] text-left md:col-span-9 md:mx-0 md:mt-12 lg:mt-24 2xl:mt-32 2xl:max-w-[494px] 3xl:mt-36'>
                {mobileWalletData.map((item, index) => (
                  <MobileWallet header={item.header} description={item.description} key={index} />
                ))}
              </div>
            </div>
          </motion.section>

          <ListedCryptos500 />

          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='mx-auto flex flex-col items-center pt-24 text-center md:pt-12 lg:pb-24 xl:pb-36 3xl:pb-48'
          >
            <h2 className='text-4xl font-bold'>Begin your journey</h2>
            <p className='mt-0 pt-4 text-center text-sm font-medium md:-mt-5 xl:text-xl xl:leading-[31px] 2xl:pt-8'>
              Download Komodo Desktop Wallet — financial
              <br /> freedom at your fingertips
            </p>
            <div className='mx-auto flex max-w-[400px] flex-wrap justify-around gap-4 pt-10 sm:max-w-none sm:items-center sm:space-x-[24px] xl:flex-nowrap xl:space-x-[42px] xl:px-0'>
              {downloadButtonWrapper(
                <Link href={KomodoWalletDownloads.windows} className='all-desktop-begin-win'>
                  <ExportedImage
                    placeholder='empty'
                    src={windows}
                    alt='windows'
                    className='h-auto'
                  />
                </Link>,
              )}
              {downloadButtonWrapper(
                <Link href={KomodoWalletDownloads.macos} className='all-desktop-begin-mac'>
                  <ExportedImage placeholder='empty' src={macos} alt='mac-os' className='h-auto' />
                </Link>,
              )}
              {downloadButtonWrapper(
                <Link href={KomodoWalletDownloads.linux} className='all-desktop-begin-linux'>
                  <ExportedImage placeholder='empty' src={linux} alt='linux' className='h-auto' />
                </Link>,
              )}
            </div>
          </motion.section>
          {/* 
          <BlocksMobileDesktop />
          <JoinCommunityMobileDesktop /> */}

          <motion.section
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className='relative mx-auto max-w-7xl py-12 sm:px-6 sm:py-16 md:pb-20 lg:px-8 lg:pb-28 3xl:pb-32'
          >
            <BgEllipse className='top-[300px] z-[-10]' />
            <motion.div
              variants={staggerChildren}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='relative mx-auto w-full max-w-[586px]'
            >
              <motion.h2
                variants={fadeInUp}
                className='text-center text-2xl font-bold lg:pb-2 lg:text-3xl xl:pb-4 3xl:pb-8 3xl:text-4xl'
              >
                FAQs
              </motion.h2>
              {faqData.map((item, index) => (
                <motion.div key={index} variants={fadeInUp}>
                  <FAQ
                    faqNo={item.faqNo}
                    faqQstn={item.faqQstn}
                    faqAnswer={item.faqAnswer}
                    selected={selected}
                    setSelected={setSelected}
                  />
                </motion.div>
              ))}
            </motion.div>
            <motion.div
              variants={fadeInUp}
              initial='initial'
              whileInView='animate'
              viewport={{ once: true }}
              className='mx-auto w-full max-w-[586px] pt-12'
            >
              <hr className='w-full' />
              <div className='mt-4 flex flex-row items-center justify-between'>
                <p className='text-sm font-medium md:text-lg'>View user guides</p>

                {downloadButtonWrapper(
                  <Link href={siteData.desktopGuidesLink} rel='follow'>
                    <motion.button
                      type='button'
                      className='font-manrope cta-btn inline-flex h-[38px] w-[176px] items-center justify-center rounded-[14px] border border-none py-3 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 2xs:w-[164px] sm:h-[50px] sm:w-[170px] sm:text-base'
                      style={{
                        backgroundImage:
                          'linear-gradient(90deg, #5A68E6 12.55%, rgba(73, 134, 234, 0.68) 93.3%)',
                      }}
                    >
                      <span className=''>Desktop Guides</span>
                      <ExportedImage
                        placeholder='empty'
                        src={footerArrow}
                        alt='footer-arrow'
                        className='ml-2 h-auto w-[10px]'
                      />
                    </motion.button>
                  </Link>,
                )}
              </div>
            </motion.div>
          </motion.section>
        </div>
        <div className='hidden lg:col-span-2 lg:block'></div>
      </main>
    </>
  );
}
